// In-memory cache of calendar month payloads, keyed the same way as
// the idb-keyval (IndexedDB) copies. Reads are synchronous, so month
// navigation renders instantly with no blank flash.
//
// The cache is an LRU: reads and writes move a key to the back, and
// writes evict from the front once the cap is passed. Without a cap,
// a session that browses years of history would hold every visited
// month in RAM until the next page reload — and this app runs on a
// shared screen that stays open for weeks.
//
// The cap is a safety limit, not something normal use ever reaches.
// The default of 120 means a session would have to browse ten years
// of months, without a page reload, before anything is evicted. At
// roughly 20-100 KB per payload the full cache is a few MB at worst.
// Eviction drops only the RAM copy — the month is still in IndexedDB
// and comes back with one async read.
//
// VITE_MONTH_CACHE_MAX_ENTRIES overrides the default. Tests set it
// very low to force eviction on every navigation. It also lets us
// lower the cap without a code change if month payloads ever grow.
// Vite reads the variable at build time, so changing it means
// rebuilding the app. Anything that is not a positive whole number
// falls back to the default.
const DEFAULT_MAX_ENTRIES = 120;
const capFromEnv = Number(import.meta.env.VITE_MONTH_CACHE_MAX_ENTRIES);
export const MONTH_CACHE_MAX_ENTRIES =
  Number.isInteger(capFromEnv) && capFromEnv > 0
    ? capFromEnv
    : DEFAULT_MAX_ENTRIES;

const cache = new Map();

// Monotonic version per key, bumped on every Pusher invalidation.
// Prefetch callbacks capture the version when they start and compare
// on arrival; a mismatch means a real-time update landed mid-flight
// and the stale response must be dropped. When a key is evicted its
// version goes too — with no cached entry there is nothing left to
// guard, and the next invalidation recreates it at 1.
//
// Asking for a key's version registers the key, so clear() can bump
// every key a fetch has ever captured — a response that started before
// a clear-everything is dropped whichever month it was for.
const versions = new Map();

export function keyFor(communityId, year, month) {
  return `community-${communityId}-calendar-${year}-${month}`;
}

// A Map remembers insertion order, so re-inserting on read makes that
// order track recency of use: eviction takes from the front, and the
// months people actually look at sit at the back.
export function get(key) {
  if (!cache.has(key)) return undefined;
  var value = cache.get(key);
  cache.delete(key);
  cache.set(key, value);
  return value;
}

export function set(key, value) {
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > MONTH_CACHE_MAX_ENTRIES) {
    var oldest = cache.keys().next().value;
    cache.delete(oldest);
    versions.delete(oldest);
    fetchedAt.delete(oldest);
  }
}

// How many months are in RAM right now. The app never reads this;
// tests use it to check the cap holds under real navigation.
export function size() {
  return cache.size;
}

// When a key was last filled from the NETWORK (never from IndexedDB —
// that copy may be days old). switchMonths reads this to skip its
// revalidation fetch when the data on hand arrived seconds ago, which
// is the case when the boot-time prefetch or an adjacent-month
// prefetch just downloaded the month. Refetching it would only repeat
// the same request.
const fetchedAt = new Map();

export function markFresh(key) {
  fetchedAt.set(key, Date.now());
}

export function isFresh(key, maxAgeMs) {
  var t = fetchedAt.get(key);
  return typeof t === "number" && Date.now() - t <= maxAgeMs;
}

// Drops the cache entry but keeps the version. Invalidation removes
// the entry and then bumps the version; an in-flight prefetch needs
// the bumped version to survive so it can see it was superseded.
export function remove(key) {
  cache.delete(key);
  fetchedAt.delete(key);
}

export function bumpVersion(key) {
  versions.set(key, (versions.get(key) || 0) + 1);
}

export function versionFor(key) {
  if (!versions.has(key)) versions.set(key, 0);
  return versions.get(key);
}

// Drop every month: a resident or unit changed, and their names are on
// chips in any month. In-flight reads and fetches that started before
// this see a different version on arrival and drop their result.
export function clear() {
  cache.clear();
  fetchedAt.clear();
  versions.forEach(function (_version, key) {
    bumpVersion(key);
  });
}
